// ?verify_email=<token>: POST auth/email/verify {token} once on mount, then
// the outcome. A rejected token offers a resend by email.
import { useEffect, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { postEmailResend, postEmailVerify } from '../../api/endpoints';
import { isApiError } from '../../api/errors';
import { useElapsedLine } from '../../states/useElapsed';
import { HonestState } from '../../states/HonestState';
import { stateOfError } from '../../states/stateOf';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Input';
import { AuthFrame } from './AuthFrame';

export interface VerifyEmailProps {
  readonly token: string;
  readonly onDone: () => void;
}

export function VerifyEmail({ token, onDone }: VerifyEmailProps) {
  const verify = useMutation({ mutationFn: postEmailVerify });
  const resend = useMutation({ mutationFn: postEmailResend });
  const [email, setEmail] = useState('');
  const elapsed = useElapsedLine(verify.isPending);
  const { mutate } = verify;
  useEffect(() => { mutate(token); }, [mutate, token]);

  const onResend = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (email.trim()) resend.mutate(email.trim());
  };

  const rejected = verify.isError && isApiError(verify.error) && verify.error.status < 500 && !verify.error.isTimeout && !verify.error.isNetwork;

  return (
    <AuthFrame title="Verify your email" where="Email verification">
      {verify.isPending ? <HonestState variant="Loading" reason="Confirming the verification token (15 s limit)." detail={elapsed} nextAction={{ label: 'Back to sign in', onSelect: onDone }} /> : null}
      {verify.isSuccess ? (
        <HonestState variant="Ready" title="Email verified" reason={verify.data.email ? `${verify.data.email} is verified. Sign in to open the workspace.` : 'Your email is verified. Sign in to open the workspace.'} nextAction={{ label: 'Go to sign in', onSelect: onDone }} />
      ) : null}
      {verify.isError ? (
        <HonestState
          variant={rejected ? 'Permission denied' : stateOfError(verify.error).variant}
          title={rejected ? 'Verification link rejected' : 'Verification unavailable'}
          reason={rejected ? 'The token is expired, already used, or malformed. Request a new email below.' : stateOfError(verify.error).reason}
          nextAction={[{ label: 'Try again', onSelect: () => verify.mutate(token) }, { label: 'Back to sign in', onSelect: onDone }]}
        />
      ) : null}
      {rejected ? (
        <form className="door-form" onSubmit={onResend} aria-label="Resend verification email">
          <h2>Send a new verification email</h2>
          <label className="door-lbl" htmlFor="verify-email">Email</label>
          <Field id="verify-email" name="email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          <div className="door-row">
            <Button variant="secondary" type="submit" disabled={resend.isPending}>{resend.isPending ? 'Sending' : 'Send'}</Button>
          </div>
          {resend.isSuccess ? <p className="door-note">If that address has a pending verification, a new email is on its way.</p> : null}
          {resend.isError ? <HonestState variant={stateOfError(resend.error).variant} reason={stateOfError(resend.error).reason} nextAction={{ label: 'Try again', onSelect: () => resend.reset() }} /> : null}
        </form>
      ) : null}
    </AuthFrame>
  );
}
